import _ from 'lodash';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { Organization } from '../types/organization';
import { AdminFacility } from '../types/admin/admin-facility';
import { AdminUser } from '../types/admin/admin-user';
import { HsApiService } from './hs-api.service';
import { SpinnerService } from './spinner.service';
import { UserValidationService } from './user-validation.service';
import { ToasterService } from './toaster.service';
import { SessionService } from './session.service';
import { ConfirmDialogService } from './confirm-dialog.service';
import { environment } from '../../environments/environment';
import { Subject } from 'rxjs';
import { AdminOrganization } from '../types/admin/admin-organization';
import { HttpClient } from '@angular/common/http';
import { ErrorsDialogService } from './errors-dialog.service';
import { SessionStorageService } from './session-storage.service';
import { AdminPreviewError } from '../types/admin/admin-preview-error';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  organizations = [];
  facilities = [];
  users = [];
  saveUserEvent = new Subject<any>();
  selectUserEvent = new Subject<any>();

  selectedUser: AdminUser;
  selectedOrg: AdminOrganization;
  selectedFac: AdminFacility;

  constructor(
    private log: LogService,
    private hsApi: HsApiService,
    private userValidation: UserValidationService,
    private spinner: SpinnerService,
    private toaster: ToasterService,
    private session: SessionService,
    private sessionStorage: SessionStorageService,
    private confirmDialog: ConfirmDialogService,
    private http: HttpClient,
    private errorsDialog: ErrorsDialogService
  ) {}

  isDevMode() {
    return environment.settings['devMode'];
  }

  reset() {
    this.users = null;

    this.selectedOrg = null;
    this.selectedFac = null;
    this.selectedUser = null;
  }

  getFacilitiesFromCodes(facilityCodes: string[]): AdminFacility[] {
    const facilities = _.filter(this.facilities, o => {
      return _.find(facilityCodes, function(s) {
        return o.FacilityCode === s;
      });
    });
    return _.sortBy(facilities, 'FacilityName');
  }

  getFacilityByCode(facilityCode: string): AdminFacility {
    if (!facilityCode) {
      return null;
    } else {
      const fac = _.find(this.facilities, function(o) {
        return o.FacilityCode === facilityCode;
      });
      return Object.assign(new AdminFacility(), fac);
    }
  }

  private getListUser(): AdminUser {
    return this.getUserByUsername(this.selectedUser.Username);
  }

  getUserByUsername(username: string): AdminUser {
    return _.find(this.users, { Username: username });
  }

  getSelectedUser(): AdminUser {
    return this.selectedUser;
  }

  selectFacility(facilityCode: string) {
    if (!facilityCode || facilityCode === 'none') {
      this.selectedFac = null;
    } else {
      this.selectedFac = this.getFacilityByCode(facilityCode);
    }
    this.log.trace('AdminService.selectFacility', this.selectedFac);
  }

  reloadSelectedUser(onComplete: Function) {
    this.loadSelectedUser(this.selectedUser.Username, onComplete);
  }

  loadSelectedUser(username: string, onComplete: Function) {
    this.hsApi
      .getUserDetails(this.selectedOrg.UserPoolId, username)
      .subscribe(data => {
        this.selectedUser = Object.assign(new AdminUser(), data);
        this.selectedUser.PreviousPanels=[...this.selectedUser.Panels];
        this.selectedUser.initialize();
        this.selectedUser.IsSelf =
          this.selectedUser.Username.toLowerCase() ===
          this.session.getUser().username.toLowerCase();

        this.log.trace(
          'AdminService.loadSelectedUser.onComplete',
          this.selectedUser
        );
        onComplete();
      }, this.handleError);
  }

  setSelectedUser(newUser?: AdminUser) {
    const currentUser = this.getSelectedUser();
    if (currentUser && currentUser.IsChanged) {
      const msg = currentUser.IsNew
        ? 'Discard updates for new user?'
        : `Discard updates for ${this.getSelectedUser().getFullName()}?`;
      this.confirmDialog.open(msg, 'Discard', 'Cancel', () => {
        currentUser.IsChanged = false;
        this.setSelectedUser(newUser);
      });
    } else {
      this.selectUserEvent.next();
      if (!newUser) {
        this.selectedUser = null;
      } else if (newUser.IsNew) {
        this.selectedUser = newUser;
      } else {
        this.spinner.showSpinner('Loading user details...');
        this.loadSelectedUser(newUser.Username, () => {
          this.spinner.hideSpinner();
        });
      }
    }
  }

  addNewUser() {
    const newUser = AdminUser.getNewUser();
    this.log.trace('AdminService.addNewUser.selectedFac', this.selectedFac);
    if (this.selectedFac) {
      newUser.addFacility(this.selectedFac);
    }
    this.log.trace('AdminService.addNewUser.newUser', newUser);
    this.setSelectedUser(newUser);
  }

  loadOrganizationDetails(userPoolId: string, onComplete: Function) {
    this.hsApi.getOrganizationDetails(userPoolId).subscribe(data => {
      this.selectedOrg = Object.assign(new AdminOrganization(), data);
      // convert facility codes into Objects
      this.selectedOrg.Facilities = this.getFacilitiesFromCodes(
        this.selectedOrg.Facilities
      );
      this.selectedOrg.initialize(data);
      this.log.trace(
        'AdminService.loadOrganizationDetails.selectedOrg',
        this.selectedOrg
      );
      if (this.isDevMode()) {
        // const result = this.selectedOrg.getPanelMap('RADNET');
        // this.log.trace('result', result);
      }
      this.loadUsers(() => {
        onComplete();
      });
    }, this.handleError);
  }

  searchUser(username: string, onComplete: Function) {
    this.hsApi.searchUser(username).subscribe(data=>{
      onComplete(data);
    },this.handleError);
  }

  saveUser() {
    // trim all spaces first
    this.getSelectedUser().trimSpaces();

    // check for validation errors
    const errors = this.userValidation.validate(
      this.selectedOrg,
      this.selectedFac,
      this.getSelectedUser()
    );
    if (errors && errors.length > 0) {
      this.log.trace('AdminService.saveUser', { errors });
      // this.toaster.showAlert(_.join(errorMsg));
      this.errorsDialog.open(errors);
      return false;
    }

    // attempt to save user
    this.spinner.showSpinner('Saving user details...');
    this.hsApi.saveUser(this.selectedOrg, this.selectedUser).subscribe(data => {
      this.log.trace('AdminService.saveUser.success', data);

      // this happens when email is in wrong format (server side validation)
      if (data.usersErrored && data.usersErrored.length > 0) {
        this.errorsDialog.open([data.usersErrored[0].error]);
        this.spinner.hideSpinner();
      } else {
        this.loadUsers(() => {
          this.reloadSelectedUser(() => {
            this.saveUserEvent.next();
            this.spinner.hideSpinner();
            this.toaster.showInfoTimed('User details saved successfully.');
          });
        });
      }
    }, this.handleError);
  }

  initialize(onComplete: Function) {
    const onLoadAllFacilitiesComplete = () => {
      const user = this.session.getUser();

      if (user.isOrgAdmin || user.isFacAdmin) {
        this.loadOrganizationDetails(user.organization.UserPoolId, () => {
          if (this.isDevMode()) {
            // this.addNewUser();
            // this.setSelectedUser(this.users[0]);
          }

          // if Facility Admin, select facility
          if (user.isFacAdmin) {
            const facCode = this.session.getUser().getFirstFacilityCode();
            this.selectedFac = this.getFacilityByCode(facCode);
            this.log.trace('AdminService.initialize.selectedFac', {
              facCode: facCode,
              selectedFac: this.selectedFac
            });
          }
          onComplete();
        });
      } else {
        onComplete();
      }
    };

    const onLoadAllOrganizationsComplete = () => {
      this.loadAllFacilities(onLoadAllFacilitiesComplete);
    };

    const onSSOComplete = () => {
      this.loadAllOrganizations(onLoadAllOrganizationsComplete);
    };

    const adminSSO = environment.applications.admin.getUrl(
      this.session,
      this.sessionStorage
    );
    this.http.post(adminSSO.url, adminSSO.body).subscribe(onSSOComplete);
  }

  loadAllOrganizations(onComplete: Function) {
    this.hsApi.getOrganizations().subscribe(data => {
      this.organizations = [];
      _.forEach(data, d => {
        const org = Object.assign(new Organization(), d);
        this.organizations.push(org);
      });

      // sort only if MXAdmin
      if (this.session.getUser().isMxAdmin) {
        this.organizations = _.sortBy(this.organizations, 'OrganizationName');
      }
      // this.log.trace('AdminService.loadAllOrganizations', this.organizations);
      onComplete();
    }, this.handleError);
  }

  loadAllFacilities(onComplete?: Function) {
    this.hsApi.getFacilities().subscribe(data => {
      // first, convert into AdminFacility objects
      this.facilities = [];
      _.forEach(data, f => {
        this.facilities.push(Object.assign(new AdminFacility(), f));
      });

      // then sort by Code
      this.facilities = _.sortBy(this.facilities, 'FacilityCode');

      //      this.log.trace('AdminService.loadAllFacilities', this.facilities);

      if (onComplete) {
        onComplete();
      }
    }, this.handleError);
  }

  loadUsers(onComplete: Function) {
    this.hsApi.getUsers(this.selectedOrg.UserPoolId).subscribe(data => {
      this.users = [];
      _.forEach(data, d => {
        let user: AdminUser;
        user = Object.assign(new AdminUser(), d);
        user.initialize();
        this.log.trace('AdminService.loadUsers.user', user);
        this.users.push(user);
      });
      this.users = _.sortBy(this.users, 'Username');
      onComplete();
    }, this.handleError);
  }

  getBulkLoadPreview(sendWelcome: boolean, file: File, onComplete: Function) {
    this.hsApi
      .getBulkLoadPreview(
        sendWelcome,
        file,
        this.selectedOrg.OrganizationId,
        this.sessionStorage.getUUXSessionId()
      )
      .subscribe((data: any) => {
        let errors = [] as AdminPreviewError[];
        const users = [] as AdminUser[];

        // In case of a general/system error in the response
        if (data.error) {
          errors.push(
            Object.assign(new AdminPreviewError(), {
              Username: '',
              Error: data.error
            })
          );
        }
        // Handle username-specific errors
        else if (data.errors && data.errors.length > 0) {
          _.forEach(data.errors, error => {
            errors.push(Object.assign(new AdminPreviewError(), error));
          });
        }
        // Otherwise push in users
        else if (data.users && data.users.length > 0) {
          _.forEach(data.users, user => {
            const adminUser = Object.assign(new AdminUser(), user);
            adminUser.IsNew = true;
            adminUser.initialize();
            users.push(adminUser);
          });
          errors = this.userValidation.validateAll(
            this.selectedOrg,
            this.selectedFac,
            users
          );
        }
        onComplete(users, errors);
      }, this.handleError);
  }

  doBulkUpload(sendWelcome: boolean, file: File, onComplete: Function) {
    this.hsApi
      .doBulkUpload(
        sendWelcome,
        file,
        this.selectedOrg.OrganizationId,
        this.sessionStorage.getUUXSessionId()
      )
      .subscribe((data: any) => {
        this.loadUsers(() => {
          onComplete();
          this.saveUserEvent.next();
        });
      }, this.handleError);
  }

  // User Actions ------------------
  actionResendWelcome() {
    this.spinner.showSpinner('Resending welcome email, please wait...');
    this.hsApi
      .resendWelcome(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed('Invitation email has been resent.');
      }, this.handleError);
  }

  actionResetPassword() {
    this.spinner.showSpinner('Resetting user password, please wait...');
    this.hsApi
      .resetPassword(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed('Password has been reset.');
      }, this.handleError);
  }

  actionLockUser() {
    this.spinner.showSpinner('Locking user account, please wait...');
    this.hsApi
      .lockUser(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.selectedUser.IsLockedOut = true;
        this.getUserByUsername(this.selectedUser.Username).IsLockedOut = true;
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed('User account locked.');
      }, this.handleError);
  }

  actionUnlockUser() {
    this.spinner.showSpinner('Unlocking user account, please wait...');
    this.hsApi
      .unlockUser(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.selectedUser.IsLockedOut = false;
        this.getUserByUsername(this.selectedUser.Username).IsLockedOut = false;
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed('User account unlocked.');
      }, this.handleError);
  }

  actionActivateUser() {
    this.spinner.showSpinner('Activating user account, please wait...');
    this.hsApi
      .activateUser(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.selectedUser.IsActive = true;
        this.getUserByUsername(this.selectedUser.Username).IsActive = true;
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed(
          'You have successfully activated the account.'
        );
      }, this.handleError);
  }

  actionDeactivateUser() {
    this.spinner.showSpinner('Deactivating user account, please wait...');
    this.hsApi
      .deactivateUser(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.selectedUser.IsActive = false;
        this.getUserByUsername(this.selectedUser.Username).IsActive = false;
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed(
          'You have successfully deactivated the account.'
        );
      }, this.handleError);
  }

  actionEnableMFA() {
    if (!this.getSelectedUser().MFAPhone) {
      this.toaster.showAlert(
        'MFA requires a valid MFA phone, please try again.'
      );
      return;
    }

    this.spinner.showSpinner('Enabling MFA for user, please wait...');
    this.hsApi
      .enableMFA(
        this.selectedOrg.UserPoolId,
        this.selectedUser.Username,
        this.selectedUser.MFAPhone
      )
      .subscribe(data => {
        this.reloadSelectedUser(() => {
          this.spinner.hideSpinner();
          this.toaster.showInfoTimed('MFA enabled for user.');
        });
      }, this.handleError);
  }

  actionDisableMFA() {
    this.spinner.showSpinner('Disabling MFA for user, please wait...');
    this.hsApi
      .disableMFA(this.selectedOrg.UserPoolId, this.selectedUser.Username)
      .subscribe(data => {
        this.selectedUser.IsMFAEnabled = false;
        this.getUserByUsername(this.selectedUser.Username).IsMFAEnabled = false;
        this.spinner.hideSpinner();
        this.toaster.showInfoTimed('MFA disabled for user.');
      }, this.handleError);
  }
  // --------------------------

  private handleError = err => {
    const response = _.get(err, 'error', err);
    this.log.error(response);
    this.spinner.hideSpinner();
    this.toaster.showAlert(
      _.get(
        response,
        'error',
        _.get(response, 'message', JSON.stringify(response))
      )
    );
  };
}